import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { RevealLine } from "@/components/motion/Reveals";
import { useLang } from "@/i18n/LangContext";
import { useContent, resolveUrl } from "@/cms/ContentContext";
import { useLeafTx } from "@/cms/useTexts";

const Hero = () => {
  const { t } = useLang();
  const { content } = useContent();
  const tx = useLeafTx();
  const HERO_IMG = resolveUrl(content?.images?.hero) || "";
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  return (
    <section
      id="inicio"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] pt-80 md:pt-[26rem] pb-8 md:pb-12 overflow-hidden"
    >
      {/* Subtle radial vignette */}
      <div className="pointer-events-none absolute inset-0" style={{
        background: "radial-gradient(1200px 600px at 80% 20%, rgba(176,146,106,0.09), transparent 60%)"
      }} />

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="overline overline-2x text-[#6B6554] mb-3 md:mb-4"
          data-testid="hero-overline"
        >
          {tx("hero.overline", t.hero.overline)}
        </motion.div>

        <div className="grid grid-cols-12 gap-4 md:gap-8 items-end">
          {/* Kinetic serif hero */}
          <motion.h1
            style={{ y: textY }}
            className="col-span-12 lg:col-span-12 font-montserrat-medium text-[#3A3524] tracking-tight leading-[1.5]"
          >
            <span className="block text-[9vw] sm:text-[7vw] lg:text-[5vw]">
              <RevealLine delay={0.1}>{tx("hero.line1", t.hero.line1)}</RevealLine>
            </span>
            <span className="block text-[9vw] sm:text-[7vw] lg:text-[5vw] text-[#8C714D]">
              <RevealLine delay={0.28}>{tx("hero.line2", t.hero.line2)}</RevealLine>
            </span>
            <span className="block text-[9vw] sm:text-[7vw] lg:text-[5vw]">
              <RevealLine delay={0.46}>{tx("hero.line3", t.hero.line3)}</RevealLine>
            </span>
            <span className="block text-[9vw] sm:text-[7vw] lg:text-[5vw]">
              <RevealLine delay={0.64}>{tx("hero.line4", t.hero.line4)}</RevealLine>
            </span>
          </motion.h1>

          {/* Parallax framed image removed */}
        </div>

        {/* Sub + CTAs */}
        <div className="mt-14 md:mt-20 grid grid-cols-12 gap-4 md:gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="col-span-12 md:col-span-7 lg:col-span-5 text-[#3A3524]/80 text-lg md:text-xl leading-relaxed font-light"
            data-testid="hero-sub"
          >
            {tx("hero.sub", t.hero.sub)}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25 }}
            className="col-span-12 md:col-span-5 lg:col-span-7 flex flex-wrap items-end justify-start md:justify-end gap-4"
          >
            <a
              href="#contacto"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 bg-[#3A3524] text-[#F5F2EC] px-8 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#8C714D] transition-colors duration-500"
            >
              {tx("hero.cta", t.hero.cta)}
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </a>
            <a
              href="#servicos"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-3 border border-[#3A3524]/40 text-[#3A3524] px-8 py-4 text-[11px] tracking-[0.28em] uppercase hover:border-[#3A3524] transition-colors duration-500"
            >
              {tx("hero.ctaAlt", t.hero.ctaAlt)}
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
